/**
 * useModuleSegments — derive an intra-module table of contents from lesson
 * markdown.
 *
 * v1 has NO backend manifest dependency. We extract segments client-side by
 * scanning the raw markdown for top-level `## ` headings (skipping fenced code
 * blocks so a `## ` inside a ```bash``` example isn't mistaken for a heading).
 *
 * Anchors MUST match the ids that rehype-slug stamps onto the rendered <h2>
 * elements, otherwise clicking a TOC entry would scroll nowhere. rehype-slug
 * uses the `github-slugger` package with a fresh slugger per document and the
 * same duplicate-suffix behaviour ("foo", "foo-1", "foo-2"). We reuse that exact
 * library here so the two derivations can never drift.
 *
 * The hook also exposes a manifest stub: `getModuleManifest()` returns null
 * gracefully today (no endpoint exists), so a future v2 can swap the segment
 * source to a server-authored manifest without touching call-sites.
 */

import { useEffect, useMemo, useState } from "react";
import GithubSlugger from "github-slugger";
import { getModuleManifest, ModuleManifest } from "../api/client";

export interface ModuleSegment {
  /** Heading text, stripped of trailing markdown (e.g. inline emphasis kept as-is). */
  title: string;
  /** Slug matching the rendered heading id from rehype-slug. */
  anchor: string;
}

/**
 * Parse top-level `## ` headings out of markdown, ignoring fenced code blocks.
 *
 * Why regex over a full markdown AST: the page already does a single
 * react-markdown render pass for the body. Re-parsing to an AST here would
 * double the work for no benefit — we only need the heading text, and the
 * fenced-code guard is the only real edge case. KISS.
 */
export function extractSegments(markdown: string | null | undefined): ModuleSegment[] {
  if (!markdown) return [];

  const slugger = new GithubSlugger();
  const segments: ModuleSegment[] = [];
  const lines = markdown.split(/\r?\n/);

  let inFence = false;
  let fenceMarker = "";

  for (const line of lines) {
    const fenceMatch = line.match(/^\s*(```+|~~~+)/);
    if (fenceMatch) {
      const marker = fenceMatch[1][0]; // "`" or "~"
      if (!inFence) {
        inFence = true;
        fenceMarker = marker;
      } else if (marker === fenceMarker) {
        inFence = false;
        fenceMarker = "";
      }
      continue;
    }
    if (inFence) continue;

    // Top-level segment headings only: exactly two hashes.
    const headingMatch = line.match(/^##\s+(.+?)\s*#*\s*$/);
    if (headingMatch) {
      const rawTitle = headingMatch[1].trim();
      // Strip common inline markdown so TOC labels read cleanly. rehype-slug
      // slugs from the heading's *text content*, which has markup removed, so
      // we mirror that by stripping markdown syntax before slugging.
      const title = stripInlineMarkdown(rawTitle);
      const anchor = slugger.slug(title);
      segments.push({ title, anchor });
    }
  }

  return segments;
}

/** Remove the inline markdown markers that don't survive into rendered text. */
function stripInlineMarkdown(text: string): string {
  return text
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/\*\*([^*]+)\*\*/g, "$1") // bold
    .replace(/\*([^*]+)\*/g, "$1") // italic
    .replace(/_([^_]+)_/g, "$1") // italic (underscore)
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // links → link text
    .trim();
}

export interface UseModuleSegmentsResult {
  segments: ModuleSegment[];
  /** Reserved for v2: server-authored manifest. Always null in v1. */
  manifest: ModuleManifest | null;
}

/**
 * Derive TOC segments for a module.
 *
 * @param markdown The unlocked lesson markdown (null when locked / coming soon).
 * @param slug     Module slug — used only by the (stubbed) manifest fetch.
 */
export function useModuleSegments(
  markdown: string | null | undefined,
  slug: string | undefined,
): UseModuleSegmentsResult {
  const segments = useMemo(() => extractSegments(markdown), [markdown]);

  // Manifest is a graceful no-op in v1. Kept as state + effect so the wiring is
  // already in place for v2 without changing the hook's public shape.
  const [manifest, setManifest] = useState<ModuleManifest | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!slug) {
      setManifest(null);
      return;
    }
    getModuleManifest(slug)
      .then((m) => {
        if (!cancelled) setManifest(m);
      })
      .catch(() => {
        if (!cancelled) setManifest(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { segments, manifest };
}

export default useModuleSegments;
